import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, CheckCircle2, ArrowRight, Sparkles, Crown,
  Target, FileText, Brain, Building2, Cloud, Download,
  AlertTriangle, Scale, EyeOff, Users, Clock, TrendingUp,
  Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useI18n } from "../i18n/I18nProvider";
import Logo from "../components/Logo";
// S10-C5: converge header duplicado en PublicNav parametrizado (ADR-S10-003)
import PublicNav from "../components/PublicNav";

const COPY = {
  en: {
    nav: { features: "Features", solutions: "Solutions", pricing: "Pricing", login: "Login", start: "Request Demo" },
    hero: {
      badge: "COMPLIANCE, ENGINEERED",
      h1_1: "Simplify Compliance.",
      h1_2: "Secure Your Enterprise.",
      subtitle: "The definitive Multi-Framework GRC platform for LATAM. ISO 27001, NIST SCF, CIS v8.1, ISO 20000, NORTIC A7/A8/A9 (Dominican Gov), and Zona Franca — without the spreadsheet chaos.",
      cta_primary: "Request a Demo",
      cta_secondary: "Sign In",
      stat1: "45+", stat1_label: "Integrated Modules",
      stat2: "16", stat2_label: "Frameworks",
      stat3: "-70%", stat3_label: "Audit Time"
    },
    social: "FRAMEWORKS SUPPORTED",
    pains: {
      label: "THE PROBLEM",
      title: "The Compliance Paradox",
      subtitle: "More frameworks, less time. Manual approaches break your security posture—and your team.",
      items: [
        { icon: Clock, title: "Manual Audits", desc: "Hundreds of hours chasing emails, updating spreadsheets, and reconciling evidence." },
        { icon: FileText, title: "Scattered Evidence", desc: "Files lost across Drive, SharePoint, and emails. Audit day becomes a treasure hunt." },
        { icon: Scale, title: "Framework Complexity", desc: "Mapping controls between ISO 27001, NIST SCF, and CIS v8.1 is a full-time job on its own." },
        { icon: Users, title: "Limited Resources", desc: "Small security teams overwhelmed by enterprise-grade requirements from every angle." },
        { icon: AlertTriangle, title: "Audit Anxiety", desc: "Constant fear of non-compliance, regulatory fines, and failed certifications." },
        { icon: EyeOff, title: "Lack of Visibility", desc: "No real-time dashboard to show the board your true security posture and progress." }
      ]
    },
    solutions: {
      label: "THE SOLUTION",
      title: "Chaos, Systematized.",
      subtitle: "ComplianceOS turns your compliance nightmare into a competitive advantage.",
      items: [
        { icon: Target, pain: "Manual Audits", title: "Automated Gap Analysis", desc: "Run full gap analysis across frameworks in seconds. See exactly where you stand, always." },
        { icon: FileText, pain: "Scattered Evidence", title: "Centralized Evidence Vault", desc: "One secure repository for every artifact, mapped to every control, audit-ready 24/7." },
        { icon: Scale, pain: "Framework Complexity", title: "Cross-Framework Mappings", desc: "Map a control once, satisfy many. Overlapping ISO/NIST/CIS requirements handled automatically." },
        { icon: Brain, pain: "Limited Resources", title: "AI Policy & Wizard", desc: "Generate auditor-ready policies in seconds. Our AI assistant guides implementation step by step." },
        { icon: CheckCircle2, pain: "Audit Anxiety", title: "Certification Readiness Score", desc: "Know exactly how ready you are for certification audit—before the auditor arrives." },
        { icon: TrendingUp, pain: "Lack of Visibility", title: "Executive Dashboard", desc: "Real-time risk metrics, compliance scores, and heat maps. Ready to present to the C-suite." }
      ]
    },
    features: {
      label: "WHAT'S INSIDE",
      title: "Everything you need for continuous compliance",
      subtitle: "35+ integrated modules covering the full GRC lifecycle",
      items: [
        { icon: Shield, title: "16 Frameworks", desc: "4 core IT, NORTIC A7/A8/A9 (Dominican Gov), 6 Zona Franca verticals, plus cross-mapping references" },
        { icon: AlertTriangle, title: "Risk Management", desc: "ISO 31000/27005 methodology, risk heat maps, AI risk predictor, vendor risk" },
        { icon: CheckCircle2, title: "Audits & NCs", desc: "Internal audits, non-conformities, corrective actions, management review" },
        { icon: Brain, title: "AI Tools", desc: "Policy generator, implementation wizard, risk predictor powered by Claude" },
        { icon: Building2, title: "Multi-Tenant", desc: "Manage multiple client organizations with isolated data per tenant" },
        { icon: Cloud, title: "Cloud Evidence", desc: "Auto-collect evidence from AWS, Azure, GCP, GitHub, and Okta" },
        { icon: Download, title: "PDF Reports", desc: "Executive reports, client dashboards, and audit-ready exports in one click" },
        { icon: Target, title: "Client Dashboard", desc: "Unified view per client with all GAP/Risk/SoA projects consolidated" }
      ]
    },
    pricing: {
      label: "PRICING",
      title: "Plans that scale with your growth",
      subtitle: "Simple, transparent pricing. Talk to us to see it in action.",
      popular: "MOST POPULAR",
      current: "Your current plan",
      tiers: [
        { id: "consultor", icon: TrendingUp, name: "Consultant", price: "$250", period: "per month", desc: "For independent GRC consultants serving multiple clients with one main framework.",
          features: ["1 framework included", "5 client tenants", "500 risks · 150 documents", "3 users", "Multi-tenant client management", "AI Policy Generator + Wizard", "PDF + SMTP exports", "Extra frameworks: $350/mo each"],
          cta: "Request a Demo" },
        { id: "profesional", icon: Crown, name: "Professional", price: "$450", period: "per month", desc: "For internal GRC departments or small consultancies covering multiple frameworks.",
          features: ["3 frameworks included", "1 tenant", "1,000 risks · 250 documents", "10 users", "Full AI suite (policy, predictor, wizard)", "Cloud evidence connectors", "Vendor & Threat Intel", "Extra frameworks: $350/mo each"],
          cta: "Request a Demo", popular: true },
        { id: "empresarial", icon: Crown, name: "Enterprise", price: "$750", period: "per month", desc: "For regulated organizations needing multiple frameworks, custom RBAC and priority support.",
          features: ["6 frameworks included", "1 tenant", "2,000 risks · 450 documents", "20 users", "RBAC custom roles", "API access", "Priority support · Dedicated onboarding", "Extra frameworks: $350/mo each"],
          cta: "Talk to Sales" }
      ]
    },
    final: {
      title: "Ready to transform your GRC?",
      subtitle: "Book a personalized walkthrough with our team in under 24 hours.",
      cta: "Request a Demo",
      note: "Personalized walkthrough · Tailored to your frameworks"
    },
    footer: {
      tagline: "Multi-Framework Compliance, Engineered.",
      product: "Product", resources: "Resources", company: "Company", legal: "Legal",
      rights: "(c) 2026 ComplianceOS by MSSPRD. All rights reserved.",
      links: {
        product: [
          { label: "Features", href: "#features" },
          { label: "Pricing", href: "#pricing" },
          { label: "Changelog", comingSoon: true },
          { label: "Roadmap", comingSoon: true }
        ],
        resources: [
          { label: "Documentation", href: "#" },
          { label: "API Reference", comingSoon: true },
          { label: "Blog", comingSoon: true },
          { label: "Support", href: "#" }
        ],
        company: [
          { label: "About", href: "#" },
          { label: "Customers", comingSoon: true },
          { label: "Partners", comingSoon: true },
          { label: "Contact", href: "#" }
        ],
        legal: [
          { label: "Privacy", href: "#" },
          { label: "Terms", href: "#" },
          { label: "Security", href: "#" },
          { label: "DPA", href: "#" }
        ]
      }
    }
  },
  es: {
    nav: { features: "Funciones", solutions: "Soluciones", pricing: "Precios", login: "Iniciar Sesion", start: "Solicitar Demo" },
    hero: {
      badge: "CUMPLIMIENTO, DISENADO",
      h1_1: "Simplifica el Cumplimiento.",
      h1_2: "Asegura tu Empresa.",
      subtitle: "La plataforma GRC multi-marco definitiva para LATAM. ISO 27001, NIST SCF, CIS v8.1, ISO 20000, NORTIC A7/A8/A9 (Gobierno RD) y Zona Franca — sin el caos de las hojas de calculo.",
      cta_primary: "Solicitar Demo",
      cta_secondary: "Iniciar Sesion",
      stat1: "45+", stat1_label: "Modulos Integrados",
      stat2: "16", stat2_label: "Marcos",
      stat3: "-70%", stat3_label: "Tiempo de Auditoria"
    },
    social: "MARCOS COMPATIBLES",
    pains: {
      label: "EL PROBLEMA",
      title: "La Paradoja del Cumplimiento",
      subtitle: "Mas marcos, menos tiempo. Los enfoques manuales fracturan tu postura de seguridad y a tu equipo.",
      items: [
        { icon: Clock, title: "Auditorias Manuales", desc: "Cientos de horas persiguiendo correos, actualizando hojas de calculo y reconciliando evidencia." },
        { icon: FileText, title: "Evidencia Dispersa", desc: "Archivos perdidos entre Drive, SharePoint y correos. El dia de auditoria se vuelve una busqueda del tesoro." },
        { icon: Scale, title: "Complejidad de Marcos", desc: "Mapear controles entre ISO 27001, NIST SCF y CIS v8.1 es un trabajo de tiempo completo." },
        { icon: Users, title: "Recursos Limitados", desc: "Pequenos equipos de seguridad abrumados por requisitos empresariales desde todos los angulos." },
        { icon: AlertTriangle, title: "Ansiedad de Auditoria", desc: "Miedo constante al incumplimiento, multas regulatorias y certificaciones fallidas." },
        { icon: EyeOff, title: "Falta de Visibilidad", desc: "Sin panel en tiempo real para mostrar a la direccion tu verdadera postura de seguridad." }
      ]
    },
    solutions: {
      label: "LA SOLUCION",
      title: "Caos, Sistematizado.",
      subtitle: "ComplianceOS convierte tu pesadilla de cumplimiento en una ventaja competitiva.",
      items: [
        { icon: Target, pain: "Auditorias Manuales", title: "Analisis de Brechas Automatizado", desc: "Ejecuta un GAP completo entre marcos en segundos. Sabe siempre donde estas parado." },
        { icon: FileText, pain: "Evidencia Dispersa", title: "Boveda Central de Evidencias", desc: "Un repositorio seguro para cada artefacto, mapeado a cada control, listo para auditoria 24/7." },
        { icon: Scale, pain: "Complejidad de Marcos", title: "Mapeo Multi-Marco", desc: "Mapea un control una vez, cumple con muchos. Requisitos superpuestos ISO/NIST/CIS resueltos automaticamente." },
        { icon: Brain, pain: "Recursos Limitados", title: "IA: Politicas y Asistente", desc: "Genera politicas listas para auditor en segundos. El asistente IA guia la implementacion paso a paso." },
        { icon: CheckCircle2, pain: "Ansiedad de Auditoria", title: "Puntaje de Preparacion", desc: "Sabe exactamente que tan listo estas para la auditoria de certificacion, antes de que llegue el auditor." },
        { icon: TrendingUp, pain: "Falta de Visibilidad", title: "Panel Ejecutivo", desc: "Metricas de riesgo en tiempo real, puntajes de cumplimiento y mapas de calor. Listo para presentar a la alta direccion." }
      ]
    },
    features: {
      label: "LO QUE INCLUYE",
      title: "Todo lo que necesitas para cumplimiento continuo",
      subtitle: "35+ modulos integrados cubriendo el ciclo GRC completo",
      items: [
        { icon: Shield, title: "16 Marcos", desc: "4 core IT, NORTIC A7/A8/A9 (Gobierno RD), 6 verticales Zona Franca, más marcos de referencia" },
        { icon: AlertTriangle, title: "Gestion de Riesgos", desc: "Metodologia ISO 31000/27005, mapas de calor, predictor IA, riesgo de proveedores" },
        { icon: CheckCircle2, title: "Auditorias y NCs", desc: "Auditorias internas, no conformidades, acciones correctivas, revision por la direccion" },
        { icon: Brain, title: "Herramientas IA", desc: "Generador de politicas, asistente de implementacion, predictor de riesgos con Claude" },
        { icon: Building2, title: "Multi-Tenant", desc: "Gestiona multiples organizaciones cliente con datos aislados por tenant" },
        { icon: Cloud, title: "Evidencia Cloud", desc: "Recoleccion automatica desde AWS, Azure, GCP, GitHub y Okta" },
        { icon: Download, title: "Reportes PDF", desc: "Reportes ejecutivos, fichas de cliente y exportaciones de auditoria con un clic" },
        { icon: Target, title: "Ficha del Cliente", desc: "Vista unificada por cliente con todos los proyectos GAP/Riesgo/SoA consolidados" }
      ]
    },
    pricing: {
      label: "PRECIOS",
      title: "Planes que escalan con tu crecimiento",
      subtitle: "Precios simples y transparentes. Conversemos para verlo en accion.",
      popular: "MAS POPULAR",
      tiers: [
        { id: "consultor", icon: TrendingUp, name: "Consultor", price: "$250", period: "por mes", desc: "Para consultores GRC independientes con un marco principal y varios clientes.",
          features: ["1 norma o estándar incluido", "5 tenants de cliente", "500 riesgos · 150 documentos", "3 usuarios", "Multi-tenant + Ficha del Cliente", "AI Policy Generator + Asistente", "Exportes PDF · Notificaciones SMTP", "Normas adicionales: $350/mes c/u"],
          cta: "Solicitar Demo" },
        { id: "profesional", icon: Crown, name: "Profesional", price: "$450", period: "por mes", desc: "Para departamentos GRC internos o consultoras pequeñas que cubren varios marcos.",
          features: ["3 normas o estándares incluidos", "1 tenant", "1.000 riesgos · 250 documentos", "10 usuarios", "Suite IA completa (políticas, predictor, asistente)", "Conectores cloud de evidencia", "Vendor Risk + Threat Intel", "Normas adicionales: $350/mes c/u"],
          cta: "Solicitar Demo", popular: true },
        { id: "empresarial", icon: Crown, name: "Empresarial", price: "$750", period: "por mes", desc: "Para organizaciones reguladas con múltiples marcos, RBAC y soporte prioritario.",
          features: ["6 normas o estándares incluidos", "1 tenant", "2.000 riesgos · 450 documentos", "20 usuarios", "Roles RBAC personalizados", "Acceso API", "Soporte prioritario · Onboarding dedicado", "Normas adicionales: $350/mes c/u"],
          cta: "Hablar con Ventas" }
      ]
    },
    final: {
      title: "Listo para transformar tu GRC?",
      subtitle: "Agenda una demostracion personalizada con nuestro equipo en menos de 24 horas.",
      cta: "Solicitar Demo",
      note: "Demostracion personalizada · Adaptada a tus marcos"
    },
    footer: {
      tagline: "Cumplimiento Multi-Marco, Disenado.",
      product: "Producto", resources: "Recursos", company: "Empresa", legal: "Legal",
      rights: "(c) 2026 ComplianceOS by MSSPRD. Todos los derechos reservados.",
      links: {
        product: [
          { label: "Funciones", href: "#features" },
          { label: "Precios", href: "#pricing" },
          { label: "Changelog", comingSoon: true },
          { label: "Roadmap", comingSoon: true }
        ],
        resources: [
          { label: "Documentacion", href: "#" },
          { label: "Referencia API", comingSoon: true },
          { label: "Blog", comingSoon: true },
          { label: "Soporte", href: "#" }
        ],
        company: [
          { label: "Acerca de", href: "#" },
          { label: "Clientes", comingSoon: true },
          { label: "Partners", comingSoon: true },
          { label: "Contacto", href: "#" }
        ],
        legal: [
          { label: "Privacidad", href: "#" },
          { label: "Terminos", href: "#" },
          { label: "Seguridad", href: "#" },
          { label: "DPA", href: "#" }
        ]
      }
    }
  }
};

// S9: set destacado honesto — quitados GDPR/SOC2/ISO27005 (no integrados)
const FEATURED_FRAMEWORKS = [
  "ISO 27001", "NIST SCF", "CIS v8.1", "ISO 20000",  // 4 core IT integrados
  "NORTIC A7", "NORTIC A8", "NORTIC A9",              // diferenciador RD gov
  "Zona Franca",                                       // guiño al vertical (OEA/CNZFE/HACCP...)
];

// Screenshots placeholder data — swap src="" for a real path when captures are ready
// Images go in frontend/public/screenshots/ — CSP img-src 'self' already allows it
const SCREENSHOTS = [
  { key: "dashboard", labelEs: "Panel Ejecutivo",             labelEn: "Executive Dashboard",          src: "/screenshots/dashboard.png" },
  { key: "gap",       labelEs: "Análisis de Brechas",         labelEn: "Gap Analysis",                 src: "/screenshots/gap.png" },
  { key: "soa",       labelEs: "Declaración de Aplicabilidad", labelEn: "Statement of Applicability",  src: "/screenshots/soa.png" },
  { key: "risks",     labelEs: "Mapa de Calor de Riesgos",    labelEn: "Risk Heatmap",                 src: "/screenshots/risk-heatmap.png" },
  { key: "mappings",  labelEs: "Mapeo entre Normas",          labelEn: "Cross-Framework Mappings",     src: "/screenshots/mappings.png" },
  { key: "reports",   labelEs: "Reportes Ejecutivos",         labelEn: "Executive Reports",            src: "/screenshots/reports.png" },
];

const LandingPage = () => {
  // setLocale eliminado: ahora lo gestiona PublicNav internamente (S10-C5)
  const { locale } = useI18n();
  const lang = locale;
  const navigate = useNavigate();
  const t = COPY[lang === "es" ? "es" : "en"];
  // menuOpen/setMenuOpen eliminados en S10-C5: los gestiona PublicNav internamente
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  return (
    <div className="min-h-screen bg-[#0A0F26] text-white overflow-hidden font-sans" data-testid="landing-page">
      {/* ===== HEADER — S10-C5: converge en PublicNav parametrizado ===== */}
      {/* navItems con /#solutions (ancla absoluta, consistente con PublicNav default).
          data-testid cambia de header-cta → public-nav-cta (ADR-S10-003). */}
      <PublicNav
        scrolled={scrolled}
        navItems={[
          { key: "features",  href: "/#features" },
          { key: "solutions", href: "/#solutions" },
          { key: "pricing",   href: "/#pricing" },
        ]}
      />

      {/* ===== HERO ===== */}
      <section className="relative pt-32 pb-24 md:pt-44 md:pb-32 lg:pt-52">
        {/* Background: radial gradient + grid */}
        <div className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            /* S9: hero glow violet (#6B46F6) + cyan (#00D4FF) — ADR-S9-004 decorative only */
            backgroundImage: `radial-gradient(circle at 50% 0%, rgba(107,70,246,0.18) 0%, transparent 55%), radial-gradient(circle at 80% 20%, rgba(0,212,255,0.07) 0%, transparent 40%), linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)`,
            backgroundSize: "100% 100%, 100% 100%, 56px 56px, 56px 56px"
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-medium text-[10px] tracking-[0.2em] px-3 py-1">
              <Sparkles className="w-3 h-3 mr-1.5" />{t.hero.badge}
            </Badge>
          </motion.div>
          <motion.h1 {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight font-heading leading-[1.05]">
            {t.hero.h1_1}
            <br />
            <span className="text-primary">{t.hero.h1_2}</span>
          </motion.h1>
          <motion.p {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto">
            {t.hero.subtitle}
          </motion.p>
          <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate("/demo")}
              className="bg-action text-action-foreground hover:bg-action/90 font-semibold h-12 px-8 group"
              data-testid="hero-cta-primary">
              {t.hero.cta_primary}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/auth")}
              className="border-white/20 text-white hover:bg-white/5 h-12 px-8 bg-transparent"
              data-testid="hero-cta-secondary">
              {t.hero.cta_secondary}
            </Button>
          </motion.div>

          {/* Stats strip */}
          <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-20 grid grid-cols-3 gap-4 max-w-2xl mx-auto border-t border-b border-white/10 py-8">
            {[
              { v: t.hero.stat1, l: t.hero.stat1_label },
              { v: t.hero.stat2, l: t.hero.stat2_label },
              { v: t.hero.stat3, l: t.hero.stat3_label }
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl sm:text-4xl font-bold font-heading text-white">{s.v}</div>
                <div className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-[0.15em] mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF ===== */}
      <section className="border-y border-white/10 py-10 bg-[#0D122E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-[10px] sm:text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">{t.social}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 sm:gap-x-12 mb-4">
            {FEATURED_FRAMEWORKS.map((fw, i) => (
              <span key={i} className="text-sm sm:text-base font-mono text-zinc-400 hover:text-white transition">{fw}</span>
            ))}
          </div>
          {/* S9: enlace a página /normas */}
          <div className="text-center mt-2">
            <Link
              to="/normas"
              className="text-xs text-primary hover:text-primary/80 transition inline-flex items-center gap-1"
              data-testid="normas-link"
            >
              {lang === "es" ? "Ver todas las normas →" : "View all frameworks →"}
            </Link>
          </div>
        </div>
      </section>

      {/* ===== PAIN POINTS ===== */}
      <section id="solutions" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 lg:gap-20">
            <motion.div {...fadeUp} className="lg:sticky lg:top-24 lg:self-start">
              <Badge variant="outline" className="border-red-500/30 text-red-400 bg-red-500/5 font-medium text-[10px] tracking-[0.2em] px-3 py-1 mb-5">
                <AlertTriangle className="w-3 h-3 mr-1.5" />{t.pains.label}
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading leading-[1.1]">
                {t.pains.title}
              </h2>
              <p className="mt-4 text-zinc-400">{t.pains.subtitle}</p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
              {t.pains.items.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="bg-[#0A0F26] p-6 sm:p-8 hover:bg-[#0D122E] transition group"
                    data-testid={`pain-${i}`}
                  >
                    <Icon className="w-6 h-6 text-red-400/80 mb-4 group-hover:text-red-400 transition" />
                    <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOLUTIONS ===== */}
      <section className="py-24 md:py-32 bg-[#0D122E] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-medium text-[10px] tracking-[0.2em] px-3 py-1 mb-5">
              <CheckCircle2 className="w-3 h-3 mr-1.5" />{t.solutions.label}
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading leading-[1.1]">
              {t.solutions.title}
            </h2>
            <p className="mt-4 text-zinc-400">{t.solutions.subtitle}</p>
          </motion.div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {t.solutions.items.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.1 }}
                  className="bg-[#0A0F26] p-6 sm:p-8 hover:bg-[#0D122E] transition group"
                  data-testid={`solution-${i}`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-10 h-10 rounded bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">{`0${i + 1}`}</span>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{item.pain}</p>
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <Badge variant="outline" className="border-blue-500/30 text-blue-400 bg-blue-500/5 font-medium text-[10px] tracking-[0.2em] px-3 py-1 mb-5">
              <Sparkles className="w-3 h-3 mr-1.5" />{t.features.label}
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading leading-[1.1]">
              {t.features.title}
            </h2>
            <p className="mt-4 text-zinc-400">{t.features.subtitle}</p>
          </motion.div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.features.items.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                  whileHover={{ y: -4 }}
                  className="border border-white/10 bg-[#0D122E] p-6 hover:border-primary/30 hover:bg-[#0F1535] transition group relative overflow-hidden"
                  data-testid={`feature-${i}`}
                >
                  <Icon className="w-7 h-7 text-primary mb-4" />
                  <h3 className="font-semibold text-base mb-2">{item.title}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SCREENSHOTS (S9: placeholders, swap src when real captures ready) ===== */}
      {/* To use real screenshots: set src="/screenshots/dashboard.png" etc. */}
      {/* Images go in frontend/public/screenshots/ — CSP img-src 'self' allows it */}
      <section className="py-24 md:py-32 bg-[#0D122E] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-medium text-[10px] tracking-[0.2em] px-3 py-1 mb-5">
              <Sparkles className="w-3 h-3 mr-1.5" />
              {lang === "es" ? "PLATAFORMA" : "PLATFORM"}
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight font-heading leading-[1.1]">
              {lang === "es" ? "Diseñada para equipos GRC modernos" : "Built for modern GRC teams"}
            </h2>
            <p className="mt-4 text-zinc-400">
              {lang === "es"
                ? "Una plataforma unificada para todos tus marcos de cumplimiento."
                : "One unified platform for all your compliance frameworks."}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SCREENSHOTS.map((s, i) => {
              const label = lang === "es" ? s.labelEs : s.labelEn;
              return (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="relative aspect-[16/10] border border-action/30 bg-[#0A0F26] overflow-hidden group"
                  data-testid={`screenshot-${s.key}`}
                  aria-label={label}
                >
                  {s.src ? (
                    <img
                      src={s.src}
                      alt={label}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    /* Placeholder — remove when real capture is available */
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-zinc-600">
                      <div className="w-8 h-8 border border-zinc-700 rounded flex items-center justify-center">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-center px-4">{label}</span>
                    </div>
                  )}
                  {/* Label overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A0F26] to-transparent px-3 py-2">
                    <span className="text-[10px] text-zinc-400">{label}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-24 md:py-32 bg-[#0D122E] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-medium text-[10px] tracking-[0.2em] px-3 py-1 mb-5">
              <Star className="w-3 h-3 mr-1.5" />{t.pricing.label}
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading leading-[1.1]">
              {t.pricing.title}
            </h2>
            <p className="mt-4 text-zinc-400">{t.pricing.subtitle}</p>
          </motion.div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.pricing.tiers.map((tier, i) => {
              const Icon = tier.icon;
              const isPopular = tier.popular;
              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`relative border bg-[#0A0F26] p-8 flex flex-col ${isPopular ? "border-primary shadow-[0_0_40px_rgba(107,70,246,0.2)] md:-my-4 md:py-12" : "border-white/10"}`}
                  data-testid={`pricing-${tier.id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-action text-action-foreground text-[10px] font-bold tracking-[0.2em]">
                      {t.pricing.popular}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-6">
                    <Icon className={`w-5 h-5 ${isPopular ? "text-primary" : "text-zinc-400"}`} />
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">{tier.name}</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-5xl font-bold font-heading">{tier.price}</span>
                    <span className="text-sm text-zinc-500">/{tier.period.split(" ")[0]}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mb-6">{tier.period}</p>
                  <p className="text-sm text-zinc-400 mb-8 min-h-[40px]">{tier.desc}</p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${isPopular ? "text-primary" : "text-zinc-400"}`} />
                        <span className="text-zinc-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    size="lg"
                    onClick={() => navigate("/demo")}
                    className={`w-full font-semibold ${
                      isPopular
                        ? "bg-action text-action-foreground hover:bg-action/90"
                        : "bg-white/5 text-white border border-white/10 hover:bg-white/10"
                    }`}
                    data-testid={`pricing-cta-${tier.id}`}
                  >
                    {tier.cta}
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeUp} className="border border-primary/20 bg-gradient-to-b from-primary/10 to-transparent p-12 md:p-20 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20 pointer-events-none"
              style={{ backgroundImage: `radial-gradient(circle at 50% 100%, rgba(107,70,246,0.3) 0%, transparent 60%)` }} />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading leading-[1.1]">
                {t.final.title}
              </h2>
              <p className="mt-4 text-zinc-400 max-w-xl mx-auto">{t.final.subtitle}</p>
              <Button
                size="lg"
                onClick={() => navigate("/demo")}
                className="mt-8 bg-action text-action-foreground hover:bg-action/90 font-semibold h-12 px-8 group"
                data-testid="final-cta"
              >
                {t.final.cta}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <p className="mt-4 text-xs text-zinc-500">{t.final.note}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-white/10 bg-[#0D122E] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-3">
                {/* S9: V2 logo inverse for footer on navy background */}
                <Logo variant="inverse" className="h-10 w-auto" alt="ComplianceOS by MSSPRD" />
              </div>
              <p className="text-xs text-zinc-500 max-w-[200px]">{t.footer.tagline}</p>
            </div>
            {["product", "resources", "company", "legal"].map(col => (
              <div key={col}>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-4">{t.footer[col]}</h4>
                <ul className="space-y-2">
                  {t.footer.links[col].map(link => (
                    <li key={link.label}>
                      {link.comingSoon ? (
                        <span
                          className="text-sm text-zinc-700 cursor-not-allowed"
                          data-coming-soon="true"
                          aria-disabled="true"
                          title={lang === "es" ? "Proximamente" : "Coming soon"}
                        >
                          {link.label}
                        </span>
                      ) : (
                        <a href={link.href || "#"} className="text-sm text-zinc-500 hover:text-white transition">
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-zinc-600">{t.footer.rights}</p>
            <div className="text-[8rem] md:text-[10rem] font-bold tracking-tight font-heading leading-none text-white/5 pointer-events-none select-none">
              ComplianceOS
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
